<script lang="ts">
// import { $vfm } from 'vue-final-modal';
import { defineComponent, PropType } from 'vue';
import { useI18n } from 'vue-i18n';
import { loadHeroesForFaction } from '@api/roster';
import type { RosterType } from '@interfaces/roster_type';
import type { StructureType } from '@interfaces/structure_type';
import type { HeroType } from '@interfaces/hero_type';
import { useLocalizedNamesStore } from '../../../stores/localizedNames';

interface ComponentData {
  selected_hero: HeroType | null;
  heroesLoading: boolean;
}

export default defineComponent({
  name: 'SelectHero',
  props: {
    roster: {
      type: Object as PropType<RosterType>,
      required: true
    },
    structure: {
      type: Object as PropType<StructureType>,
      required: true
    }
  },
  emits: ["select-step"],
  data(): ComponentData {
    return {
      selected_hero: this.structure.hero,
      heroesLoading: false,
    };
  },
  computed: {
    heroes(): HeroType[] {
      return this.structure.faction?.available_heroes?.sort((a, b) => (a.weight ?? 1) - (b.weight ?? 1)) || [];
    },
    current_hero() {
      return this.structure.hero;
    },
  },
  watch: {
    locale() {
      this.ensureHeroNamesLoaded();
    },
    'structure.faction': {
      handler() {
        this.loadHeroesIfNeeded();
        this.ensureHeroNamesLoaded();
      },
      deep: false,
      immediate: true
    },
    heroes() {
      this.ensureHeroNamesLoaded();
    },
  },
  mounted() {
    this.loadHeroesIfNeeded();
    this.ensureHeroNamesLoaded();
  },
  methods: {
    async loadHeroesIfNeeded(): Promise<void> {
      const faction = this.structure.faction;
      if (!faction) return;
      if (faction.available_heroes && faction.available_heroes.length > 0) return;

      this.heroesLoading = true;
      try {
        const heroes = await loadHeroesForFaction(
          faction.id ?? null,
          faction.tech_name ?? undefined
        );
        this.structure.faction = { ...faction, available_heroes: heroes };
      } finally {
        this.heroesLoading = false;
      }
    },
    selectHero(hero: HeroType): void {
      if (this.structure) {
        this.structure.units = [];
        this.structure.spells = [];
        this.structure.equipment = [];
        this.structure.hero = hero;
        this.structure.point_cost = hero.point_cost;

        this.$emit('select-step', 'selectUnit');
      }
    },
    heroName(hero: HeroType): string {
      const id = hero.id ?? undefined;
      const techName = hero.tech_name ?? undefined;
      return (
        (id ? this.localizedNamesStore.getName('hero', undefined, id) : null) ||
        (techName ? this.localizedNamesStore.getName('hero', techName) : null) ||
        hero.name ||
        ''
      );
    },
    async ensureHeroNamesLoaded(): Promise<void> {
      const heroes = this.heroes;
      if (!heroes?.length) return;

      const items = heroes
        .filter((h) => h.id !== null || h.tech_name)
        .map((h) => ({ type: 'hero' as const, id: h.id ?? undefined, tech_name: h.tech_name ?? undefined }));

      await this.localizedNamesStore.ensureNamesLoaded(items, this.locale);
    },
  },
  setup() {
    const i18n = useI18n();
    return {
      t: i18n.t,
      locale: i18n.locale,
      localizedNamesStore: useLocalizedNamesStore(),
    };
  },
});
</script>

<template>
  <h1 class="roaster-title">{{ t('select_hero.title') }}</h1>
  <div v-if="heroesLoading" class="text-center">{{ t('select_domain.loading') }}...</div>
  <div
    v-else
    v-for="hero in heroes"
    :key="hero.id ?? hero.tech_name"
    class="element hero"
    @click="selectHero(hero)"
    :class="{ active: selected_hero?.id === hero.id || selected_hero?.tech_name === hero.tech_name }">
    <div class="w-100">
      <div class="hero-image" :class="{'centered': !hero.description}">
        <img v-image-fallback="hero.image_path"
             class="img-fluid img-hero"
             :alt="heroName(hero) || 'Hero image'"/>
      </div>
    </div>
    <h2 class="text-center">{{ heroName(hero) }}</h2>
    <div class="w-100 mh-20">
      <p class="text-center">{{ t('select_hero.cost', { cost: hero.point_cost }) }}</p>
    </div>
  </div>
</template>
